<template>
  <div class="arp-cadastradas">
    <b-table :data="atas" :paginated="atas.length > 10" :per-page="10" detailed detail-key="id">
      <template #empty>
        <section class="section">
          <div class="content has-text-grey has-text-centered">
            <p>Nenhuma ARP cadastrada.</p>
          </div>
        </section>
      </template>

      <b-table-column field="numero" label="Item" sortable v-slot="props">
        <span class="arp-cadastradas__item">Nº {{ formatNumero(props.row) }}</span>
      </b-table-column>

      <b-table-column label="Carona" v-slot="props">
        <span
          class="arp-cadastradas__carona"
          :class="permiteCarona(props.row) ? 'is-sim' : 'is-nao'"
        >
          <b-icon :icon="permiteCarona(props.row) ? 'check' : 'close'" size="is-small" />
          <span>{{ permiteCarona(props.row) ? 'Permite' : 'Não Permite' }}</span>
        </span>
      </b-table-column>

      <b-table-column label="Assinatura" v-slot="props">
        <b-tag v-if="isRascunho(props.row)" type="is-warning" size="is-small">
          Aguardando Assinatura
        </b-tag>
        <span v-else>{{ formatAssinatura(props.row) }}</span>
      </b-table-column>

      <b-table-column label="Vigência" v-slot="props">
        {{ formatVigencia(props.row) }}
      </b-table-column>

      <b-table-column
        label="Ações"
        width="420"
        header-class="arp-cadastradas__acoes-header"
        cell-class="arp-cadastradas__acoes-cell"
        v-slot="props"
      >
        <div class="arp-cadastradas__acoes">
          <template v-if="isRascunho(props.row)">
            <button
              type="button"
              class="button is-primary is-outlined is-small arp-cadastradas__btn-texto"
              @click="$emit('download', props.row)"
            >
              <b-icon icon="download" size="is-small" />
              <span>Baixar ARP</span>
            </button>
            <button
              type="button"
              class="button is-primary is-small arp-cadastradas__btn-texto"
              @click="$emit('enviar-assinada', props.row)"
            >
              <b-icon icon="upload" size="is-small" />
              <span>Enviar ARP Assinada</span>
            </button>
          </template>

          <b-tooltip
            v-else
            :label="isIntegrado(props.row) ? 'Integrado' : 'Aguardando Integrações'"
            position="is-top"
          >
            <span
              class="arp-cadastradas__status-icon"
              :class="isIntegrado(props.row) ? 'is-ok' : 'is-pending'"
              aria-hidden="true"
            >
              <b-icon :icon="isIntegrado(props.row) ? 'check' : 'clock-outline'" size="is-small" />
            </span>
          </b-tooltip>

          <div class="buttons has-addons arp-cadastradas__acoes-grupo">
            <b-tooltip
              v-if="!isRascunho(props.row) && props.row.pncpUrl"
              label="Abrir no PNCP"
              position="is-top"
            >
              <a
                class="button is-info"
                :href="props.row.pncpUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                <b-icon icon="link" />
              </a>
            </b-tooltip>
            <b-tooltip label="Editar" position="is-top">
              <button type="button" class="button is-primary" @click="$emit('editar', props.row)">
                <b-icon icon="pencil" />
              </button>
            </b-tooltip>
            <b-tooltip label="Excluir" position="is-top">
              <button type="button" class="button is-danger" @click="$emit('excluir', props.row)">
                <b-icon icon="delete" />
              </button>
            </b-tooltip>
          </div>
        </div>
      </b-table-column>

      <template #detail="props">
        <div class="arp-cadastradas__detail">
          <ArpIntegradaDetail
            v-if="isIntegrado(props.row)"
            :ata="props.row"
            :desabilitado="interacaoDesabilitada"
            @carregar-documento="$emit('carregar-documento', $event)"
            @excluir-documento="$emit('excluir-documento', $event)"
            @adicionar-parte="$emit('adicionar-parte', $event)"
            @excluir-parte="$emit('excluir-parte', $event)"
          />
          <template v-else>
            <p class="arp-cadastradas__detail-row">
              <strong>Fornecedor:</strong>
              <span>{{ props.row.fornecedorNome || '—' }}</span>
            </p>
            <p class="arp-cadastradas__detail-row">
              <strong>Assinatura:</strong>
              <span v-if="isRascunho(props.row)">Aguardando Assinatura</span>
              <span v-else>{{ formatAssinatura(props.row) }}</span>
            </p>
            <p class="arp-cadastradas__detail-row">
              <strong>Vigência:</strong>
              <span>{{ formatVigencia(props.row) }}</span>
            </p>
            <p class="arp-cadastradas__detail-row has-text-grey">
              Documentos e partes envolvidas ficam disponíveis após a integração com o PNCP.
            </p>
          </template>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import { formatarDataFormularioIso } from '@/utils/format';
import ArpIntegradaDetail from '../components/ArpIntegradaDetail';

export default {
  name: 'ArpCadastradasTable',
  components: {
    ArpIntegradaDetail,
  },
  props: {
    atas: {
      type: Array,
      default: () => [],
    },
    interacaoDesabilitada: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    isRascunho(ata) {
      if (ata.dataAssinatura) return false;
      if (ata.estado === 'PUBLICADA') return false;
      return true;
    },
    isIntegrado(ata) {
      if (ata.pncpIntegrado === true) return true;
      if (ata.integrationStatus === 'INTEGRADO') return true;
      if (ata.sequencialAta != null && String(ata.sequencialAta).trim() !== '') {
        return true;
      }
      return false;
    },
    permiteCarona(ata) {
      return (
        ata.possibilidadeAdesao === true ||
        ata.possibilidadeAdesao === 'true' ||
        ata.possibilidadeAdesao === 1
      );
    },
    formatNumero(ata) {
      const n = String(ata.numero ?? '');
      const ano = ata.ano ?? '';
      if (/^\d+$/.test(n)) {
        return `${n.padStart(3, '0')}/${ano}`;
      }
      return `${n}/${ano}`;
    },
    formatAssinatura(ata) {
      return formatarDataFormularioIso(ata.dataAssinatura) || '—';
    },
    formatVigencia(ata) {
      const a = formatarDataFormularioIso(ata.dataVigenciaInicio) || '—';
      const b = formatarDataFormularioIso(ata.dataVigenciaFim) || '—';
      return `${a} a ${b}`;
    },
  },
};
</script>

<style scoped>
.arp-cadastradas >>> .table td,
.arp-cadastradas >>> .table th {
  vertical-align: middle;
}

.arp-cadastradas >>> .table th.arp-cadastradas__acoes-header {
  text-align: right;
}

.arp-cadastradas >>> .table td.arp-cadastradas__acoes-cell {
  text-align: right;
}

.arp-cadastradas >>> .table td .tag,
.arp-cadastradas >>> .table td .button,
.arp-cadastradas >>> .table td .buttons,
.arp-cadastradas >>> .table td .b-tooltip {
  margin-bottom: 0;
  vertical-align: middle;
}

.arp-cadastradas >>> .table td .icon {
  vertical-align: middle;
}

.arp-cadastradas__item {
  color: #2153b5;
  font-weight: 700;
  line-height: 1.25;
}

.arp-cadastradas__carona {
  align-items: center;
  color: #363636;
  display: inline-flex;
  font-size: 0.875rem;
  gap: 0.35rem;
  line-height: 1.25;
}

.arp-cadastradas__carona >>> .icon {
  height: 1.125rem;
  width: 1.125rem;
}

.arp-cadastradas__carona.is-sim >>> .icon {
  color: #48c78e;
}

.arp-cadastradas__carona.is-nao >>> .icon {
  color: #f14668;
}

.arp-cadastradas__acoes {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 0.5rem;
  justify-content: flex-end;
  min-height: 2.25rem;
}

.arp-cadastradas__acoes >>> .b-tooltip {
  align-items: center;
  display: inline-flex;
  height: 2.25rem;
  line-height: 1;
  margin: 0;
}

.arp-cadastradas__acoes >>> .b-tooltip > .tooltip-trigger {
  align-items: center;
  display: inline-flex;
  height: 100%;
}

.arp-cadastradas__btn-texto {
  align-items: center;
  display: inline-flex;
  height: 2.25rem;
  margin: 0;
}

.arp-cadastradas__acoes-grupo {
  align-items: center;
  display: inline-flex;
  flex-wrap: nowrap;
  height: 2.25rem;
  margin: 0;
}

.arp-cadastradas__acoes-grupo > .b-tooltip {
  align-items: center;
  display: inline-flex;
  height: 2.25rem;
  margin: 0;
}

.arp-cadastradas__acoes-grupo > .b-tooltip .button {
  border-radius: 0;
  height: 2.25rem;
  margin: 0;
  min-width: 2.25rem;
  padding-left: 0.65em;
  padding-right: 0.65em;
}

.arp-cadastradas__acoes-grupo > .b-tooltip:first-child .button {
  border-bottom-left-radius: 4px;
  border-top-left-radius: 4px;
}

.arp-cadastradas__acoes-grupo > .b-tooltip:last-child .button {
  border-bottom-right-radius: 4px;
  border-top-right-radius: 4px;
}

.arp-cadastradas__acoes-grupo > .b-tooltip:not(:first-child) .button {
  margin-left: -1px;
}

.arp-cadastradas__acoes-grupo > .b-tooltip .button:hover,
.arp-cadastradas__acoes-grupo > .b-tooltip .button.is-hovered,
.arp-cadastradas__acoes-grupo > .b-tooltip .button:focus,
.arp-cadastradas__acoes-grupo > .b-tooltip .button:active {
  z-index: 2;
}

.arp-cadastradas__status-icon {
  align-items: center;
  display: inline-flex;
  height: 2.25rem;
  justify-content: center;
  line-height: 1;
  width: 2.25rem;
}

.arp-cadastradas__status-icon >>> .icon {
  height: 1.5rem;
  width: 1.5rem;
}

.arp-cadastradas__status-icon.is-pending >>> .icon {
  color: #f2a900;
}

.arp-cadastradas__status-icon.is-ok >>> .icon {
  color: #48c78e;
}

.arp-cadastradas__detail {
  color: #4a4a4a;
  font-size: 0.875rem;
  padding: 0;
}

.arp-cadastradas >>> .detail-container {
  padding: 0.5rem 0.25rem;
}

.arp-cadastradas__detail-row {
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
  padding: 0 1rem;
}

.arp-cadastradas__detail-row:last-child {
  margin-bottom: 0;
}

.arp-cadastradas__detail-row strong {
  flex: 0 0 auto;
}

.arp-cadastradas__detail-row span {
  flex: 1 1 auto;
  min-width: 0;
}
</style>
